import { ssoStartUrl } from '@/lib/api';
import type { SsoProviderId } from '@/lib/types';
import { cn } from '@/lib/utils';

export const SSO_LABEL: Record<SsoProviderId, string> = { microsoft: 'Microsoft', google: 'Google' };

export function ProviderIcon({ provider, className }: { provider: SsoProviderId; className?: string }) {
  if (provider === 'microsoft') {
    return (
      <svg viewBox="0 0 21 21" className={cn('size-4', className)} aria-hidden>
        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 48 48" className={cn('size-4', className)} aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.3 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z" />
      <path fill="#FBBC05" d="M10.5 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z" />
      <path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.5-5.8c-2.1 1.4-4.8 2.3-8.1 2.3-6.3 0-11.6-3.8-13.5-9.1l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
    </svg>
  );
}

/**
 * "Continue with Microsoft / Google" links. Plain anchors: the API answers the start URL with a redirect to
 * the identity provider, so the whole round-trip is a top-level navigation and no token ever meets JavaScript.
 */
export function SsoButtons({ providers, returnTo, className, size = 'lg' }: { providers: SsoProviderId[]; returnTo?: string | null; className?: string; size?: 'lg' | 'md' }) {
  if (providers.length === 0) return null;
  return (
    <div className={cn('grid gap-2.5', className)}>
      {providers.map((p) => (
        <a
          key={p}
          href={ssoStartUrl(p, returnTo)}
          className={cn(
            'border-input bg-background hover:bg-muted/60 text-foreground focus-visible:ring-ring inline-flex w-full items-center justify-center gap-3 border text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none',
            size === 'lg' ? 'h-11 px-4' : 'h-9 px-3',
          )}
        >
          <ProviderIcon provider={p} />
          Continue with {SSO_LABEL[p]}
        </a>
      ))}
    </div>
  );
}
